import React from 'react'
import styled from 'styled-components'
import { Tooltip } from '@mui/material'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { useNavigate } from 'react-router-dom'
import { ink, line, radius, surface, font, transition } from 'features/dashboard/tokens'

const BuildingPassportList = ({
  buildings = [],
  onOpen,
  onRemove,
  removeLabel = 'Remove',
  inset = true,
  renderLead,
  renderMeta,
  links,
  className,
}) => {
  const navigate = useNavigate()

  const open = bp => (onOpen ? onOpen(bp) : navigate(`/bp-list/${bp.id}`))

  return (
    <BpList className={className} $inset={inset}>
      {buildings.map(bp => {
        const address = [bp.street, bp.city, bp.postalCode].filter(Boolean).join(' · ')
        return (
          <BpRow key={bp.id} onClick={() => open(bp)} title={`Open ${bp.name}`}>
            <BpAccent />
            {renderLead && renderLead(bp)}
            <BpInfo>
              <BpName>{bp.name}</BpName>
              {address && <BpAddr>{address}</BpAddr>}
              {renderMeta && <BpMeta>{renderMeta(bp)}</BpMeta>}
            </BpInfo>
            {links && (
              <BpLinks>
                {links(bp).map(link => (
                  <BpLink
                    key={link.label}
                    type='button'
                    onClick={event => { event.stopPropagation(); link.onClick() }}
                  >
                    {link.label}
                  </BpLink>
                ))}
              </BpLinks>
            )}
            {onRemove && (
              <Tooltip title={removeLabel}>
                <RemoveBtn
                  type='button'
                  aria-label={`${removeLabel}: ${bp.name}`}
                  onClick={event => { event.stopPropagation(); onRemove(bp.id) }}
                >
                  <RemoveCircleOutlineIcon style={{ fontSize: 18 }} />
                </RemoveBtn>
              </Tooltip>
            )}
            <OpenArrow aria-hidden='true'><ArrowForwardRoundedIcon style={{ fontSize: 17 }} /></OpenArrow>
          </BpRow>
        )
      })}
    </BpList>
  )
}

export default BuildingPassportList

const BpList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: ${({ $inset }) => ($inset ? '14px' : '0')};
`

const BpRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 11px 14px;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  cursor: pointer;
  transition: ${transition};

  &:hover {
    border-color: ${({ theme }) => `${theme.colors.primary}55`};
    box-shadow: 0 4px 14px -10px rgba(12, 18, 41, 0.25);
  }
`

const BpAccent = styled.span`
  flex-shrink: 0;
  width: 3px;
  align-self: stretch;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.primary};
  opacity: 0.5;
`

const BpInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const BpName = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BpAddr = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RemoveBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: ${radius.sm};
  background: transparent;
  color: ${ink.mute};
  cursor: pointer;
  transition: ${transition};

  &:hover { color: #b00020; background: #fdecea; }
`

const OpenArrow = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: ${ink.mute};
  opacity: 0.55;
  transition: ${transition};

  ${BpRow}:hover & {
    opacity: 1;
    color: ${({ theme }) => theme.colors.primary};
  }
`

const BpMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 3px;
  font-size: ${font.caption};
`

const BpLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    padding-top: 8px;
    border-top: 1px solid ${line.base};
  }
`

const BpLink = styled.button`
  font-family: inherit;
  min-height: 30px;
  padding: 4px 12px;
  border: 1px solid ${line.base};
  border-radius: ${radius.pill};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    border-color: #33becc;
    color: #1f8c97;
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`
